/**
 * The board's one data hook. Both the hub card and the full page use it, so the
 * two can never drift apart on what "posting failed" means.
 *
 * The rule that shapes this file: LOSING WHAT SOMEONE TYPED IS THE WORST BUG HERE.
 * So `post()` throws on failure and never touches the caller's draft — the composer
 * keeps the text and puts it straight back in the box. The optimistic message that
 * appeared while the request was in flight is removed, so nobody is left believing
 * a note is on the fridge when it isn't.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { Identity, Message } from '../../shared/types';
import { explainError } from './shared';

/** A message that exists only in this browser, waiting on the server. Negative ids never collide. */
export interface PendingMessage {
  tempId: number;
  body: string;
  created_at: string;
}

export interface Board {
  messages: Message[];
  pending: PendingMessage[];
  /**
   * How many notes are on the board in total, straight from the API's COUNT.
   * `null` means WE DO NOT KNOW (the count call failed) — render no number at all
   * rather than one derived from this truncated page, which could only ever be wrong.
   */
  total: number | null;
  loading: boolean;
  /** Read failure. A failed read is NEVER rendered as an empty board. */
  error: string | null;
  reload: () => void;
  /** Resolves with the saved message; throws a human-readable Error if it didn't save. */
  post: (body: string) => Promise<Message>;
  edit: (id: number, body: string) => Promise<Message>;
  remove: (id: number) => Promise<void>;
}

export function useBoard(limit = 50): Board {
  const [messages, setMessages] = useState<Message[]>([]);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const nextTempId = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get<Message[]>(`/messages?limit=${limit}`).then(
      (rows) => {
        if (cancelled) return;
        setMessages(rows);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Leave `messages` alone: a refresh that fails must not wipe what is on screen.
        setError(explainError(err, 'Could not load the board.'));
        setLoading(false);
      }
    );

    // The board is the list; the count is only ever used to say how many notes are
    // NOT on screen. So a failed count is not a failed board — it just means the
    // number is unknown, and an unknown number is shown as no number.
    api.get<{ total: number }>('/messages/count').then(
      (row) => {
        if (cancelled) return;
        setTotal(Number.isInteger(row?.total) && row.total >= 0 ? row.total : null);
      },
      () => {
        if (cancelled) return;
        setTotal(null);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [limit, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const post = useCallback(async (body: string): Promise<Message> => {
    const text = body.trim();
    if (!text) throw new Error('Write something first.');

    const tempId = nextTempId.current--;
    const optimistic: PendingMessage = {
      tempId,
      body: text,
      created_at: new Date().toISOString(),
    };
    setPending((p) => [optimistic, ...p]);

    try {
      const saved = await api.post<Message>('/messages', { body: text });
      setMessages((prev) => (prev.some((m) => m.id === saved.id) ? prev : [saved, ...prev]));
      // The server confirmed the row. One more note is on the board — that is an
      // established fact, not a guess, so the count can move with it.
      setTotal((t) => (t == null ? null : t + 1));
      return saved;
    } catch (err) {
      // It is NOT on the fridge. Say so by taking the bubble back down, and let the
      // composer put the words back in the box.
      throw new Error(explainError(err, 'Your message didn’t post.'));
    } finally {
      setPending((p) => p.filter((m) => m.tempId !== tempId));
    }
  }, []);

  const edit = useCallback(async (id: number, body: string): Promise<Message> => {
    const text = body.trim();
    if (!text) throw new Error('Write something first.');
    try {
      const saved = await api.put<Message>(`/messages/${id}`, { body: text });
      setMessages((prev) => prev.map((m) => (m.id === saved.id ? saved : m)));
      return saved;
    } catch (err) {
      throw new Error(explainError(err, 'That edit didn’t save.'));
    }
  }, []);

  const remove = useCallback(async (id: number): Promise<void> => {
    try {
      await api.del<Message>(`/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      // Confirmed gone from the board, so the total is one lower.
      setTotal((t) => (t == null ? null : Math.max(0, t - 1)));
    } catch (err) {
      throw new Error(explainError(err, 'That message wasn’t removed.'));
    }
  }, []);

  return { messages, pending, total, loading, error, reload, post, edit, remove };
}

/** Who may edit / take down a given message. The Worker enforces this again — this is UI courtesy. */
export function canEdit(message: Message, me: Identity): boolean {
  return me.memberId != null && message.member_id === me.memberId;
}

export function canDelete(message: Message, me: Identity): boolean {
  return canEdit(message, me) || me.role === 'adult';
}
